import { useForm } from 'vee-validate'
import type { CasinoGameItem } from '@kira-dancer/nuxt-common'
import { AppDataEnum } from '@kira-dancer/nuxt-common/enums'
import { CASINO_LOBBY_CATEGORY_ALL, GAME_CASINO_LIMIT } from '~/constants'
import { RoutesEnum } from '~/enums'
import {
  CATEGORIES_CASINO,
  CATEGORIES_CASINO_ALL_OPTION,
  PROVIDER_CASINO_ALL,
} from '~/resources'
import type { DropdownOption } from '~/types/app'
import type { CategoryCasino } from '~/types/game'
import { useCasinoProvider } from '~/composables/game/use-casino-provider'

export function useLiveCasinoFilter() {
  const PROVIDER_CASINO_KEY_ALL = 'all'
  const PROVIDER_CASINO_INDEX = 1
  const route = useRoute()
  const { casinoProviders } = useCasinoProvider()

  const router = useRouter()
  const getProviderImage = (provider: string) => {
    const iconImgUrl = '/assets/images/live-casino'
    const globImages: GlobImageI = import.meta.glob(
      '~/assets/images/live-casino/*',
      {
        eager: true,
      },
    )

    const filePath = Object.keys(globImages).find((key) =>
      key.includes(provider),
    )
    const fileExtension = filePath?.split('.').pop() ?? 'svg'

    return getImageURL(globImages, provider, iconImgUrl, fileExtension)
  }

  const { requestFetchLiveCasino, fetchCasinoProviders } = useCasino({
    gameLimit: GAME_CASINO_LIMIT,
  })

  const currentCategory = computed(() => {
    return (route.params?.cats as string) ?? ''
  })

  const currentProvider = computed(() => {
    return (route.params?.prov as string) ?? PROVIDER_CASINO_KEY_ALL
  })

  const { setFieldValue } = useForm({
    initialValues: {
      provider: currentProvider.value,
      category: currentCategory.value,
    },
  })

  const categories = computed(() => {
    return CATEGORIES_CASINO[currentProvider.value] || []
  })

  const { data: allGames } = useNuxtData(
    AppDataEnum.LiveCasinoIndirect + route.path,
  )

  const { data: providerDataSSR } = useNuxtData(
    AppDataEnum.LiveCasinoProviders + route.path,
  )

  const isGameInCategory = (game: CasinoGameItem, category: CategoryCasino) =>
    game.category?.includes(category.slug)

  const categoryDisplays = computed(() => {
    const filterCategories = categories.value.filter(
      (category) =>
        allGames.value?.some(
          (game: CasinoGameItem) =>
            isGameInCategory(game, category) &&
            (currentProvider.value === PROVIDER_CASINO_KEY_ALL ||
              game.provider === currentProvider.value),
        ) || category.slug === CASINO_LOBBY_CATEGORY_ALL,
    )
    if (filterCategories?.length) {
      return filterCategories
    }
    return CATEGORIES_CASINO_ALL_OPTION
  })

  const providerTabs = computed(
    () =>
      [...PROVIDER_CASINO_ALL, ...(providerDataSSR.value ?? [])].map(
        (item) => ({
          tabId: item.slug,
          title: item.name,
          icon: item.slug,
          provider: item.slug,
        }),
      ) ?? [],
  )

  const providerOptions = computed(
    () =>
      [...PROVIDER_CASINO_ALL, ...(providerDataSSR.value ?? [])].map(
        (item) => ({
          key: item.slug,
          title: item.nameMobile ?? item.name,
          icon: item.slug,
        }),
      ) ?? [],
  )

  const categoryOptions = computed<DropdownOption[]>(() => {
    return categoryDisplays.value.map((item) => ({
      key: item.slug,
      title: item.nameMb ?? item.name,
    }))
  })

  const onClickProvider = (provider: string): void => {
    setFieldValue('category', '')
    setFieldValue('provider', provider)
    router.replace(`${RoutesEnum.LiveCasino}/${provider}`)
  }

  const requestGetAllGames = async () => {
    await useAsyncData(
      AppDataEnum.LiveCasinoIndirect + route.path,
      async () => {
        try {
          const responseGames = await requestFetchLiveCasino({
            limit: -1,
          })
          return responseGames ?? []
        } catch {
          return []
        }
      },
    )
  }

  const fetchSSRCasinoProviders = async () => {
    await useAsyncData(
      AppDataEnum.LiveCasinoProviders + route.path,
      async () => {
        if (casinoProviders.value?.length) {
          return casinoProviders.value
        }
        try {
          const responseGames = await fetchCasinoProviders({})
          return responseGames?.filter((item) => item.count > 0) ?? []
        } catch {
          return []
        }
      },
    )
  }

  fetchSSRCasinoProviders()
  requestGetAllGames()

  const changeCategory = async (categorySlug: string): Promise<void> => {
    const path = categorySlug
      ? `${RoutesEnum.LiveCasino}/${currentProvider.value}/${categorySlug}`
      : `${RoutesEnum.LiveCasino}/${currentProvider.value}`

    router.replace(path)
  }

  watch(
    () => currentProvider.value,
    (value) => {
      setFieldValue('provider', value)
    },
  )

  watch(
    () => currentProvider.value,
    () => {
      setFieldValue('category', '')
    },
  )

  onMounted(() => {
    const hasCurrentProvider = providerTabs.value.find(
      (item) => item.provider === currentProvider.value,
    )
    const hasCurrentCategory = categoryDisplays.value.find(
      (item) => item.slug === currentCategory.value,
    )

    if (
      providerTabs.value.length > PROVIDER_CASINO_INDEX &&
      !hasCurrentProvider
    ) {
      navigateTo(RoutesEnum.NotFound)
    }
    if (
      currentCategory.value &&
      !hasCurrentCategory &&
      categoryDisplays.value.length > PROVIDER_CASINO_INDEX
    ) {
      navigateTo(RoutesEnum.NotFound)
    }
  })
  return {
    changeCategory,
    onClickProvider,
    getProviderImage,
    categoryDisplays,
    currentCategory,
    currentProvider,
    providerTabs,
    categoryOptions,
    providerOptions,
  }
}
